/**
 * Shared JSON extraction and repair utilities for itinerary generation.
 * Used by both web and mobile to parse AI responses that may be
 * truncated, markdown-wrapped, or otherwise malformed.
 *
 * These functions are platform-agnostic — no React, no DOM.
 */

/**
 * Extract and parse JSON from an AI response text.
 *
 * Handles:
 * - Markdown-wrapped JSON (```json ... ```)
 * - Balanced-brace extraction (finds the outermost JSON object)
 * - Truncated response repair (closes unclosed braces/brackets,
 *   removes trailing incomplete key-value pairs)
 *
 * @param {string} text - Raw AI response text
 * @param {Object} [options]
 * @param {Function} [options.onRepair] - Called with a message when repair is attempted
 * @returns {Object|null} Parsed JSON object, or null if no valid JSON found
 */
export function extractAndRepairJSON(text, options = {}) {
  if (!text || typeof text !== 'string') return null;

  const { onRepair } = options;

  // Strip markdown fences
  let cleaned = text.trim();
  cleaned = cleaned.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();

  // Find the first opening brace
  const firstBrace = cleaned.indexOf('{');
  if (firstBrace === -1) return null;

  // Balanced-brace extraction — walks the string tracking depth,
  // properly handling string literals and escape sequences
  let jsonStr = null;
  let depth = 0;
  let inString = false;
  let escape = false;

  for (let i = firstBrace; i < cleaned.length; i++) {
    const ch = cleaned[i];
    if (escape) { escape = false; continue; }
    if (ch === '\\' && inString) { escape = true; continue; }
    if (ch === '"') { inString = !inString; continue; }
    if (inString) continue;
    if (ch === '{') depth++;
    if (ch === '}') {
      depth--;
      if (depth === 0) {
        jsonStr = cleaned.slice(firstBrace, i + 1);
        break;
      }
    }
  }

  // If truncated (unclosed braces remain), attempt repair
  if (!jsonStr && depth > 0) {
    if (onRepair) onRepair(`Attempting JSON repair: ${depth} unclosed braces`);

    let partial = cleaned.slice(firstBrace);

    // Remove trailing incomplete key-value pair
    partial = partial.replace(/,\s*"[^"]*"?\s*:?\s*("(?:[^"\\]|\\.)*)?$/, '');
    // Remove trailing incomplete object
    partial = partial.replace(/,\s*\{[^}]*$/, '');
    // Remove trailing incomplete array
    partial = partial.replace(/,\s*\[[^\]]*$/, '');
    // Remove trailing comma
    partial = partial.replace(/,\s*$/, '');

    // Close any remaining open brackets and braces
    const brackets = [];
    let rInStr = false;
    let rEsc = false;
    for (let i = 0; i < partial.length; i++) {
      const c = partial[i];
      if (rEsc) { rEsc = false; continue; }
      if (c === '\\' && rInStr) { rEsc = true; continue; }
      if (c === '"') { rInStr = !rInStr; continue; }
      if (rInStr) continue;
      if (c === '{') brackets.push('}');
      else if (c === '[') brackets.push(']');
      else if (c === '}' || c === ']') brackets.pop();
    }

    partial += brackets.reverse().join('');
    jsonStr = partial;

    if (onRepair) onRepair(`Repaired JSON (closed ${brackets.length} brackets)`);
  }

  if (!jsonStr) return null;

  try {
    return JSON.parse(jsonStr);
  } catch (e) {
    if (onRepair) onRepair(`JSON.parse failed after extraction: ${e.message}`);
    return null;
  }
}
